import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

import { formatMegabytes, pluralize } from "@/lib/utils"

interface BridgeMessage {
  event: string
  data: Record<string, unknown>
}

interface OfflineItem {
  itemId: string
  title: string
  creatorName: string
  imageUrl?: string
  position?: number
}

interface OfflineContainer {
  id: string
  spaceId: string
  spaceUrl: string
  path?: string
  title?: string
  imageUrl?: string
  itemCount: number
  creatorName?: string
  creatorAvatarUrl?: string
}

interface StateChange {
  itemId: string
  state: string
  progress?: number | null
}

// Stimulus JSON-parses action params, so a numeric-looking id arrives as a
// number — itemId crosses the bridge as a string, always.
interface ItemAction {
  params: { itemId: string | number }
}

const VERSION = 1

// Download intents carry title and creator because the download endpoint
// returns bytes only — this is native's one chance to learn them.
export default class extends BridgeComponent {
  static component = "offline"
  static targets = ["row", "progress"]
  static values = { items: Array, container: Object, totalByteSize: Number }

  declare readonly rowTargets: HTMLElement[]
  declare readonly progressTargets: HTMLElement[]
  declare readonly itemsValue: OfflineItem[]
  declare readonly containerValue: OfflineContainer
  declare readonly hasContainerValue: boolean
  declare readonly totalByteSizeValue: number

  connect() {
    super.connect()
    this.send(
      "connect",
      {
        version: VERSION,
        items: this.itemsValue.map(({ itemId }) => ({ itemId })),
      },
      (message: BridgeMessage) => this.receive(message),
    )
  }

  download({ params }: ItemAction) {
    const item = this.itemsValue.find(
      ({ itemId }) => itemId === String(params.itemId),
    )
    if (!item) return

    this.send("download", {
      version: VERSION,
      ...item,
      container: this.#container(),
    })
  }

  downloadAll() {
    if (!window.confirm(this.#downloadAllPrompt())) return

    this.send("downloadAll", {
      version: VERSION,
      container: this.#container(),
      items: this.itemsValue,
    })
  }

  pause({ params }: ItemAction) {
    this.#command("pause", params.itemId)
  }

  resume({ params }: ItemAction) {
    this.#command("resume", params.itemId)
  }

  cancel({ params }: ItemAction) {
    this.#command("cancel", params.itemId)
  }

  remove({ params }: ItemAction) {
    this.#command("remove", params.itemId)
  }

  receive(message: BridgeMessage) {
    if (message.event !== "stateChanged") return

    const { itemId, state, progress } = message.data as unknown as StateChange
    const row = this.#matching(this.rowTargets, itemId)[0]
    if (!row) return

    row.dataset.offlineState = state

    if (state !== "none" && progress == null) return

    const percent = state === "none" ? 0 : Math.round(progress! * 100)
    this.#matching(this.progressTargets, itemId).forEach((ring) =>
      ring.setAttribute("value", String(percent)),
    )
  }

  #container() {
    return this.hasContainerValue && this.containerValue.id
      ? this.containerValue
      : undefined
  }

  #command(event: string, itemId: string | number) {
    this.send(event, { version: VERSION, itemId: String(itemId) })
  }

  #matching(targets: HTMLElement[], itemId: string) {
    return targets.filter((target) => target.dataset.itemId === itemId)
  }

  #downloadAllPrompt() {
    const what = pluralize(this.itemsValue.length, "experience")
    if (this.totalByteSizeValue === 0) {
      return `Make all ${what} available offline?`
    }

    const size = formatMegabytes(this.totalByteSizeValue)
    return `Make all ${what} available offline? This uses about ${size}.`
  }
}
